import React, {
  createRef, useEffect, useState, useLayoutEffect, useMemo,
} from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { useOnScreen } from '@uncinc/uncinc-react-kitchen-sink';

import { SCREEN_TABLET, SCROLL_POSITION_THRESHOLD } from '../../config/constants';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import useScrollDirection from '../../hooks/useScrollDirection';
import useScrollToCurrentWeek from './hooks/useScrollToCurrentWeek';
import {
  BlockCta,
  BlockStreak,
  Button,
  Main,
  Popup,
  BarGraphOverview,
} from '../../components';
import useProgrammaticFocus from '../../hooks/useProgrammaticFocus';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useDailyRegistrations from '../../hooks/useDailyRegistrations';
import { viewGet } from '../../store/actions/Entity/View';

import './style.scss';

const PageRecoveryPlan: React.FC = () => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { buildClass, joinClasses } = useClasses('PageRecoveryPlan');
  const dispatch = useTypedDispatch();
  const [showPlanChangedModal, setShowPlanChangedModal] = useState(false);

  useEffect(() => {
    const planChanged = sessionStorage.getItem('planChanged');
    if (planChanged === 'true') {
      setShowPlanChangedModal(true);
      sessionStorage.removeItem('planChanged');
    }
  }, []);

  useEffect(() => {
    dispatch(viewGet('exercise_answers', 'default'));
  }, [dispatch]);

  const {
    registrationsGroupedByWeek,
    highestAmountOrGoal,
    currentWeekNumber,
    weeksBeforeProgramStartDate,
  } = useDailyRegistrations();

  const isInitialLoaded = useSelector<ImmutableRootState<boolean>, boolean>(
    (state) => state.getIn(['registrations', 'isInitialLoaded'], false),
  );

  const currentWeekRef = createRef<HTMLDivElement>();
  const currentWeekIsOnScreen = useOnScreen(currentWeekRef);

  const currentWeekOffsetTop = useScrollToCurrentWeek({
    ref: currentWeekRef,
    hasLoaded: isInitialLoaded,
  });

  const { direction, scrollPosition, setScrollPosition } = useScrollDirection({
    threshold: SCROLL_POSITION_THRESHOLD as number,
  });

  const hasReachedTop = useMemo(() => (
    scrollPosition <= SCROLL_POSITION_THRESHOLD
  ), [scrollPosition]);

  const showToTodayButton = useMemo(() => (
    isInitialLoaded && !currentWeekIsOnScreen
  ), [currentWeekIsOnScreen, isInitialLoaded]);

  const blockCtaClassNames = c({
    'PageRecoveryPlan__BlockCta--hide': !hasReachedTop && direction === 'down',
    'PageRecoveryPlan__BlockCta--is-moving': !hasReachedTop,
  });

  const focusToCurrentWeek = useProgrammaticFocus(
    currentWeekRef,
    {
      querySelector: 'h2',
    },
  );

  useLayoutEffect(() => {
    if (!currentWeekOffsetTop) {
      return;
    }

    setScrollPosition(window.scrollY);

    focusToCurrentWeek();
  }, [currentWeekOffsetTop, setScrollPosition]);

  return (
    <Page className={buildClass()} title={t('pages.recovery_plan.title')} scrollToTop={false}>
      {width < SCREEN_TABLET && (
        <div className={joinClasses(buildClass('sidebar-wrapper'))}>
          <aside className="sidebar">
            <BlockStreak
              className={buildClass('BlockStreak')}
              variant={!hasReachedTop ? 'small' : undefined}
            />

            <BlockCta
              className={joinClasses(buildClass('BlockCta'), blockCtaClassNames)}
              hideMessage
            />
          </aside>
        </div>
      )}

      <Main className="col-md-7 col-lg-6 py-8 py-lg-12">
        <h1 className="sr-only">{t('pages.recovery_plan.title')}</h1>

        <button
          type="button"
          onClick={() => {
            focusToCurrentWeek();
          }}
          className={buildClass('to-today-skiplink')}
        >
          {t('buttons.to_today')}
        </button>

        <BarGraphOverview
          ref={currentWeekRef}
          registrationsGroupedByWeek={registrationsGroupedByWeek}
          highestAmountOrGoal={highestAmountOrGoal}
          currentWeekNumber={currentWeekNumber}
          weeksBeforeProgramStartDate={weeksBeforeProgramStartDate}
          isLoading={!isInitialLoaded}
        />

        {showToTodayButton && (
          <div className={buildClass('to-today-button')}>
            <Button
              secondary
              onClick={() => {
                focusToCurrentWeek();
              }}
            >
              {t('buttons.to_today')}
            </Button>
          </div>
        )}
      </Main>

      {width >= SCREEN_TABLET && (
        <div className={joinClasses(
          buildClass('sidebar-wrapper'),
          'col-md-5 col-lg-4 py-md-8 py-lg-12',
        )}
        >
          <aside className="sidebar">
            <BlockStreak
              className={buildClass('BlockStreak')}
              variant={!hasReachedTop ? 'small' : undefined}
            />

            <BlockCta
              className={joinClasses(buildClass('BlockCta'), blockCtaClassNames)}
              hideMessage
            />
          </aside>
        </div>
      )}

      {showPlanChangedModal && (
        <Popup
          onClose={() => setShowPlanChangedModal(false)}
          title={t('pages.recovery_plan.change_plan_title')}
          closeButtonText={t('pages.recovery_plan.button_text')}
        >
          <p>{t('pages.recovery_plan.change_plan_text')}</p>
        </Popup>
      )}

    </Page>
  );
};

export default PageRecoveryPlan;
